/**
 * Form to add a new Telegram user
 */
import { useState } from 'react';
import { UserPlus, AlertCircle } from 'lucide-react';
import { useCreateTelegramUser } from '../hooks/useAdminUsers';
import { haptic } from '@/shared/utils/telegram';

interface AddUserFormProps {
  onSuccess?: () => void;
}

export function AddUserForm({ onSuccess }: AddUserFormProps) {
  const [telegramUserId, setTelegramUserId] = useState('');
  const [role, setRole] = useState<'admin' | 'viewer'>('viewer');
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState('');

  const createMutation = useCreateTelegramUser();

  const validateTelegramId = (id: string): boolean => {
    // Must be digits only and reasonable length
    const digitsOnly = /^\d+$/;
    return digitsOnly.test(id) && id.length >= 5 && id.length <= 15;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!validateTelegramId(telegramUserId)) {
      setError('Telegram ID must be 5-15 digits');
      haptic.notification('error');
      return;
    }

    try {
      haptic.impact('medium');
      await createMutation.mutateAsync({
        telegramUserId,
        role,
        isActive,
      });

      // Success
      haptic.notification('success');
      setTelegramUserId('');
      setRole('viewer');
      setIsActive(true);
      onSuccess?.();
    } catch (err) {
      haptic.notification('error');
      setError(err instanceof Error ? err.message : 'Failed to add user');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-[var(--tg-theme-secondary-bg-color)] rounded-2xl p-5 space-y-4 border border-black/5 dark:border-white/5">
      <div className="flex items-center gap-2">
        <UserPlus size={18} className="text-[var(--tg-theme-link-color)]" />
        <h3 className="text-sm font-black text-[var(--tg-theme-text-color)] uppercase tracking-wider">
          Add New User
        </h3>
      </div>

      {/* Telegram User ID */}
      <div className="space-y-2">
        <label className="text-xs font-bold text-[var(--tg-theme-hint-color)] uppercase tracking-wider">
          Telegram User ID *
        </label>
        <input
          type="text"
          value={telegramUserId}
          onChange={(e) => setTelegramUserId(e.target.value.trim())}
          placeholder="123456789"
          className="w-full h-11 px-4 bg-[var(--tg-theme-bg-color)] rounded-xl text-[13px] font-medium placeholder:text-[var(--tg-theme-hint-color)] focus:outline-none focus:ring-2 focus:ring-[var(--tg-theme-link-color)]/20"
          required
        />
        <p className="text-[10px] text-[var(--tg-theme-hint-color)]">
          Get user ID from @userinfobot on Telegram
        </p>
      </div>

      {/* Role Select */}
      <div className="space-y-2">
        <label className="text-xs font-bold text-[var(--tg-theme-hint-color)] uppercase tracking-wider">
          Role
        </label>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as 'admin' | 'viewer')}
          className="w-full h-11 px-4 bg-[var(--tg-theme-bg-color)] rounded-xl text-[13px] font-bold focus:outline-none focus:ring-2 focus:ring-[var(--tg-theme-link-color)]/20"
        >
          <option value="viewer">Viewer (read-only)</option>
          <option value="admin">Admin (full access)</option>
        </select>
      </div>

      {/* Active Toggle */}
      <div className="flex items-center justify-between py-2">
        <label className="text-xs font-bold text-[var(--tg-theme-hint-color)] uppercase tracking-wider">
          Active
        </label>
        <button
          type="button"
          onClick={() => setIsActive(!isActive)}
          className={`relative w-12 h-6 rounded-full transition-colors ${
            isActive ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'
          }`}
        >
          <div
            className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white transition-transform ${
              isActive ? 'translate-x-6' : 'translate-x-0'
            }`}
          />
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-rose-500/10 border border-rose-500/20 rounded-xl">
          <AlertCircle size={16} className="text-rose-600 flex-shrink-0" />
          <p className="text-xs font-medium text-rose-600">{error}</p>
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={createMutation.isPending}
        className="w-full py-3 px-4 bg-[var(--tg-theme-link-color)] text-white rounded-xl text-sm font-bold uppercase tracking-wide hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {createMutation.isPending ? 'Adding...' : 'Add User'}
      </button>
    </form>
  );
}
